import React from "react";
import * as FiIcons from "react-icons/fi";
import { useAuthStore } from "../../store/authStore";
const { FiSearch, FiDownload } = FiIcons;

const experienceLevels = [
  { value: "intern", label: "Intern" },
  { value: "entry", label: "Freshers" },
  { value: "mid", label: "Mid" },
  { value: "senior", label: "Senior" }
];
const jobTypes = [
  { value: "full-time", label: "Full-time" },
  { value: "part-time", label: "Part-time" },
  { value: "internship", label: "Internship" }
];
const salaryRanges = [
  { label: "₹0–₹4L", min: 0, max: 400000 },
  { label: "₹4L–₹10L", min: 400000, max: 1000000 },
  { label: "₹10L+", min: 1000000, max: null }
];

const API_BASE_URL = import.meta.env.VITE_API_PORT;

export default function PostFilter({
  selectedFilters,
  setSelectedFilters,
  onSearch,
  currentCommunity,
}) {
  const role = useAuthStore((state) => state.role);

  function handleChange(e) {
    setSelectedFilters((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  }

  function handleSalaryChange(e) {
    setSelectedFilters((prev) => ({ ...prev, salaryRange: e.target.value }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (currentCommunity) {
      setSelectedFilters((prev) => ({
        ...prev,
        community: currentCommunity,
      }));
    }
    onSearch();
  }

  function stripHTML(html) {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  }

  async function handleDownload() {
    try {
      const params = new URLSearchParams();
      if (selectedFilters.salaryRange)
        params.append("salaryLabel", selectedFilters.salaryRange);
      
      if (currentCommunity) {
        params.append("community", currentCommunity);
      }

      if (selectedFilters.keyword) params.append("keyword", selectedFilters.keyword);
      if (selectedFilters.location) params.append("location", selectedFilters.location);
      if (selectedFilters.experienceLevel) params.append("experienceLevel", selectedFilters.experienceLevel);
      if (selectedFilters.jobType) params.append("jobType", selectedFilters.jobType);
      if (selectedFilters.companyName) params.append("companyName", selectedFilters.companyName);

      // Fetch first page to get total pages
      const firstResp = await fetch(`${API_BASE_URL}/api/posts?${params.toString()}&page=1`);
      const firstPayload = await firstResp.json();
      
      if (!firstPayload.posts || firstPayload.posts.length === 0) {
        alert("No posts found to download");
        return;
      }

      const totalPages = firstPayload.totalPages || 1;
      const pagesToFetch = totalPages >= 10 ? 10 : Math.min(totalPages, 5);
      
      let allPosts = [...firstPayload.posts];
      
      if (pagesToFetch > 1) {
        const pagePromises = [];
        for (let page = 2; page <= pagesToFetch; page++) {
          pagePromises.push(
            fetch(`${API_BASE_URL}/api/posts?${params.toString()}&page=${page}`)
              .then(res => res.json())
              .then(data => data.posts || [])
          );
        }
        const additionalPages = await Promise.all(pagePromises);
        const validPosts = additionalPages.filter(posts => posts.length > 0);
        allPosts = allPosts.concat(...validPosts);
      }
      
      // Fetch job_description for each post
      const postsWithDescriptions = await Promise.all(
        allPosts.map(async (post) => {
          try {
            const descResp = await fetch(`${API_BASE_URL}/api/posts/${post._id}/job-description`);
            const descData = await descResp.json();
            return {
              ...post,
              job_description: descData.job_description || ""
            };
          } catch (err) {
            console.error(`Failed to fetch description for post ${post._id}:`, err);
            return {
              ...post,
              job_description: ""
            };
          }
        })
      );

      // Apply salary filter based on salaryMax
      let filteredPosts = postsWithDescriptions;
      if (selectedFilters.salaryRange) {
        const selectedRange = salaryRanges.find(r => r.label === selectedFilters.salaryRange);
        if (selectedRange) {
          filteredPosts = postsWithDescriptions.filter(post => {
            // If post has no salaryMax, include it (undisclosed salaries)
            if (!post.salaryMax) return true;
            
            const maxSalary = post.salaryMax;
            
            // For ranges with upper limit (e.g., ₹0–₹4L, ₹4L–₹10L)
            if (selectedRange.max !== null) {
              return maxSalary >= selectedRange.min && maxSalary <= selectedRange.max;
            }
            // For open-ended ranges (e.g., ₹10L+)
            else {
              return maxSalary >= selectedRange.min;
            }
          });
        }
      }
      
      if (filteredPosts.length === 0) {
        alert("No posts found matching the salary criteria");
        return;
      }

      const csvHeaders = [
        "Company Name",
        "Location",
        "Salary Min",
        "Salary Max",
        "Job Type",
        "Experience Level",
        "Job Title",
        "Job Description",
        "Community"
      ];

     
     const csvRows = [
        csvHeaders.join(","),
        ...filteredPosts
          .filter(post => {
            // Only include posts that have all required fields
            const hasCompanyAndLocation = post && 
                   post._id && 
                   post.companyName && 
                   post.location;
            
            if (!hasCompanyAndLocation) return false;
            
            // Check if both salaries are missing/null/undefined
            const salaryMinMissing = post.salaryMin === null || post.salaryMin === undefined;
            const salaryMaxMissing = post.salaryMax === null || post.salaryMax === undefined;
            
            // Exclude if both are missing (only for "Freshers - India" community)
            if (currentCommunity === "Freshers - India" && salaryMinMissing && salaryMaxMissing) {
              return false;
            }
            
            // Exclude if both are 0
            if (post.salaryMin === 0 && post.salaryMax === 0) return false;
            
            return true;
          })
          .map(post =>
            [
              `"${post.companyName}"`,
              `"${post.location}"`,
              post.salaryMin || "",
              post.salaryMax || "",
              `"${post.jobType || ""}"`,
              `"${post.experienceLevel || ""}"`,
              `"${post.jobTitle || ""}"`,
              `"${(post.job_description || "").replace(/"/g, '""').replace(/\n/g, ' ').replace(/\r/g, '')}"`,
              `"${post.community || ""}"`
            ].join(",")
          )
      ];

      const csvContent = csvRows.join("\n");

      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);

      const today = new Date();
      const dateStr = today.toISOString().split('T')[0];

      const communityFileName = currentCommunity 
        ? currentCommunity
            .toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9-]/g, '')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '')
        : 'jobs';

      const a = document.createElement("a");
      a.href = url;
      a.download = `${communityFileName}-jobs-${dateStr}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Download failed");
      console.error(err);
    }
  }

  return (
    <form
      className="flex flex-wrap md:flex-nowrap items-center gap-2 p-2 bg-zinc-900 rounded-xl shadow"
      onSubmit={handleSubmit}
    >
      <input
        name="keyword"
        value={selectedFilters.keyword || ""}
        onChange={handleChange}
        placeholder="Keyword"
        className="flex-1 min-w-[110px] px-2 py-2 rounded-xl bg-gray-800 text-gray-300 border border-gray-700 focus:ring-[#79e708]"
      />
      <input
        name="location"
        value={selectedFilters.location || ""}
        onChange={handleChange}
        placeholder="Location"
        className="flex-1 min-w-[90px] px-2 py-2 rounded-xl bg-gray-800 text-gray-300 border border-gray-700 focus:ring-[#79e708]"
      />
      <select
        name="experienceLevel"
        value={selectedFilters.experienceLevel || ""}
        onChange={handleChange}
        className="min-w-[100px] px-2 py-2 rounded-xl bg-gray-800 text-gray-300 border border-gray-700 focus:ring-[#79e708]"
      >
        <option className="text-gray-300 bg-gray-800" value="">
          Experience Level
        </option>
        {experienceLevels.map(lvl => (
          <option key={lvl.value} value={lvl.value} className="text-gray-300 bg-gray-800">
            {lvl.label}
          </option>
        ))}
      </select>
      <select
        name="jobType"
        value={selectedFilters.jobType || ""}
        onChange={handleChange}
        className="min-w-[100px] px-2 py-2 rounded-xl bg-gray-800 text-gray-300 border border-gray-700 focus:ring-[#79e708]"
      >
        <option className="text-gray-300 bg-gray-800" value="">
          Job Type
        </option>
        {jobTypes.map(type => (
          <option key={type.value} value={type.value} className="text-gray-300 bg-gray-800">
            {type.label}
          </option>
        ))}
      </select>
      <input
        name="companyName"
        value={selectedFilters.companyName || ""}
        onChange={handleChange}
        placeholder="Company Name"
        className="flex-1 min-w-[110px] px-2 py-2 rounded-xl bg-gray-800 text-gray-300 border border-gray-700 focus:ring-[#79e708]"
      />
      <div style={{ display: "flex", alignItems: "center" }}>
        <select
          name="salaryRange"
          value={selectedFilters.salaryRange || ""}
          onChange={handleSalaryChange}
          className="min-w-[100px] px-2 py-2 rounded-xl bg-gray-800 text-gray-300 border border-gray-700 focus:ring-[#79e708]"
        >
          <option className="text-gray-300 bg-gray-800" value="">
            Salary Range
          </option>
          {salaryRanges.map((sr) => (
            <option
              key={sr.label}
              value={sr.label}
              className="text-gray-300 bg-gray-800"
            >
              {sr.label}
            </option>
          ))}
        </select>
        {role === "tpo" && (
          <button
            type="button"
            onClick={handleDownload}
            className="ml-2 px-2 py-2 rounded-xl flex items-center justify-center border border-[#4d6a4d] bg-[#2c3c2d] hover:bg-[#3b5c41] text-[#c5f07a] shadow"
            title="Download matching posts"
            aria-label="Download matching posts"
            style={{ minWidth: 0 }}
          >
            <FiDownload className="w-5 h-5" />
          </button>
        )}
      </div>
      <button
        type="submit"
        className="
          p-2 rounded-xl flex items-center justify-center 
          bg-gradient-to-br from-[#486a56] via-[#5a8452] to-[#3b5c41]
          border border-[#4d6a4d]
          shadow-[0_0_8px_2px_rgba(121,231,8,0.4)]
          hover:shadow-[0_0_12px_4px_rgba(121,231,8,0.6)]
          backdrop-blur-md
          transition-all duration-200 ease-in-out
          hover:scale-105
          focus:outline-none focus:ring-2 focus:ring-[#79e708]
        "
        title="Search"
        aria-label="Search"
      >
        <FiSearch className="w-5 h-5 text-[#d4f38f]" />
      </button>
    </form>
  );
}